import { createRoot } from "react-dom/client";
import { initPwa } from "@/lib/pwa";
import { initTelemetry, reportError } from "@/lib/telemetry";
import { Component, ErrorInfo, ReactNode } from "react";
import App from "./App";
import "./index.css";

// Apply user-configured accessibility preferences before first paint.
try {
  const raw = localStorage.getItem("a11y-prefs-v1");
  if (raw) {
    const p = JSON.parse(raw);
    const root = document.documentElement;
    if (p?.reduceMotion) root.classList.add("a11y-reduce-motion");
    if (p?.largeText) root.classList.add("a11y-large-text");
    if (p?.highContrast) root.classList.add("a11y-high-contrast");
  }
} catch {}

const CHUNK_RELOAD_KEY = "chunk-reload-once";
const CHUNK_ERROR_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "Loading chunk",
  "Importing a module script failed",
  "Unable to preload CSS",
];
function isChunkError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return CHUNK_ERROR_PATTERNS.some((p) => err.message.includes(p));
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    if (isChunkError(error) && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
      window.location.reload();
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("React Error Boundary caught an error:", error, errorInfo);
    // Task #555 — feed React render crashes into the Engineer telemetry sink
    // with a best-effort screenshot + masked DOM snapshot for repro.
    try {
      void reportError(error.message, error.stack || errorInfo.componentStack || null, {
        severity: "error",
      });
    } catch {
      /* swallow — telemetry must never break the boundary */
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "20px", fontFamily: "sans-serif" }}>
          <h1 style={{ color: "red" }}>Something went wrong</h1>
          <pre style={{ background: "#f0f0f0", padding: "10px", overflow: "auto" }}>
            {this.state.error?.toString()}
          </pre>
          <pre style={{ background: "#f0f0f0", padding: "10px", overflow: "auto", fontSize: "12px" }}>
            {this.state.error?.stack}
          </pre>
          <button onClick={() => window.location.reload()}>Reload Page</button>
        </div>
      );
    }

    return this.props.children;
  }
}

initPwa();
try {
  initTelemetry();
} catch {
  /* swallow — telemetry init must never block app boot */
}

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);
